import { useCallback } from 'react';
import { useHandleApiResponse, useToast } from '../../../hooks';
import { useEditFolderMutation } from '../../../services';
import { conditionalObject } from '../../../utils';
import { AlertType, TextButton } from '../../shared';

interface SaveFolderButtonProps {
  folderId: string;
  folderName: string;
  folderParentId: string | null;
  nameInput: string;
  selectedFolderId: string | null;
  closeModal: () => void;
  onMoveSuccess: () => void;
}

const SaveFolderButton = ({
  folderId,
  folderName,
  folderParentId,
  nameInput,
  selectedFolderId,
  closeModal,
  onMoveSuccess,
}: SaveFolderButtonProps) => {
  const [editFolder, { isLoading: isEditingFolder }] = useEditFolderMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Disable the button if no name was entered (folder name is required) or no changes were made.
  const disabled = !nameInput || (folderName === nameInput && selectedFolderId === folderParentId);

  const onSave = useCallback(async () => {
    const isMovingFolder = selectedFolderId !== folderParentId;
    try {
      const payload = {
        id: folderId,
        name: nameInput,
        ...conditionalObject(selectedFolderId !== folderParentId, {
          // Only update the parent ID if it has changed.
          // This is in case the user is editing the name without moving the folder.
          parentId: selectedFolderId,
        }),
      };
      const response = await editFolder(payload);
      handleApiResponse({
        response,
        onSuccess: () => {
          if (isMovingFolder) onMoveSuccess();
          closeModal();
        },
      });
    } catch (error) {
      console.error(error);
      showToast({
        message: 'An unknown error occurred while editing the folder. Please try again.',
        type: AlertType.ERROR,
      });
    }
  }, [
    folderId,
    folderParentId,
    nameInput,
    selectedFolderId,
    closeModal,
    editFolder,
    handleApiResponse,
    onMoveSuccess,
    showToast,
  ]);

  return <TextButton disabled={disabled} loading={isEditingFolder} text="Save" onClick={onSave} />;
};

export default SaveFolderButton;
